import { defineStore } from 'pinia';
import { navigateTo, useNuxtApp } from 'nuxt/app';
import { ref } from 'vue';
import { setErrorNotification } from '~~/common/utils/helper';
import type { UserType } from '~~/common/stores/user';
import { useUserStore } from '~~/common/stores/user';
import { useAuthenticationStore } from '~~/common/stores/authentication';
import type { ResponseDataDealer } from '~~/common/types/api';

type MeStoreResponse = ResponseDataDealer<'/api/v2/me/store', 'get'>;
type StoresResponse = ResponseDataDealer<'/api/v2/stores', 'get'>;
type PutSessionStoreResponse = ResponseDataDealer<'/api/v2/me/session/store', 'put'>;
type GetSessionStoreResponse = ResponseDataDealer<'/api/v2/me/session/store', 'get'>;

interface DealerUserStoreState {
  userId?: UserType['id'];
  store?: MeStoreResponse;
}

let storePromise: Promise<StoresResponse> | null = null;
let dealerUserStorePromise: {
  key: UserType['id'] | null;
  promise: Promise<MeStoreResponse> | null;
} = {
  key: null,
  promise: null,
};

export const useAuchanStoresStore = defineStore('auchanStore', () => {
  const nuxtApp = useNuxtApp();

  const userStore = useUserStore();
  const authenticationStore = useAuthenticationStore();

  const dealerUserStore = ref<DealerUserStoreState>({});
  const stores = ref<StoresResponse>([]);
  const sessionStore = ref<GetSessionStoreResponse>(null);
  const sessionStoreInProgress = ref(false);

  function setDealerUserStore ({
    userId,
    store,
  }: DealerUserStoreState) {
    dealerUserStore.value = {
      userId,
      store,
    };
  }

  function setAuchanStores (payload: StoresResponse) {
    stores.value = payload;
  }

  function setSessionAuchanStore (payload: GetSessionStoreResponse) {
    sessionStore.value = payload;
  }

  function setSessionAuchanStoreInProgress (payload: boolean) {
    sessionStoreInProgress.value = payload;
  }

  async function fetchDealerUserStore () {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      const userId = userStore?.data?.id;
      if (dealerUserStorePromise.promise && dealerUserStorePromise.key === userId) {
        await dealerUserStorePromise.promise;
      } else if (userId !== dealerUserStore.value?.userId) {
        if (userId) {
          const promise = $api.$get<MeStoreResponse>('/me/store');
          dealerUserStorePromise = {
            key: userId,
            promise,
          };
          setDealerUserStore({
            userId,
            store: await promise,
          });
          dealerUserStorePromise = {
            key: null,
            promise: null,
          };
        } else {
          setDealerUserStore({});
        }
      }
    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error });
    }
  }

  async function fetchAuchanStores () {
    const {
      $api,
      $logger,
    } = nuxtApp;

    if (!storePromise) {
      try {
        storePromise = $api.$get<StoresResponse>('stores');
        setAuchanStores(await storePromise);
      } catch (error) {
        $logger.error(error);
        setErrorNotification({ nuxtApp, error });
      }
    } else {
      return storePromise;
    }
  }

  async function fetchSessionAuchanStore () {
    const {
      $api,
      $logger,
    } = nuxtApp;

    try {
      setSessionAuchanStoreInProgress(true);
      const result = await $api.$get<GetSessionStoreResponse>('me/session/store');
      setSessionAuchanStore(result);
    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error });
    }
    setSessionAuchanStoreInProgress(false);
  }

  async function pushSessionAuchanStore (storeCode: string) {
    const {
      $api,
      $logger,
    } = nuxtApp;

    await authenticationStore.reInitToken();

    try {
      setSessionAuchanStoreInProgress(true);
      const result = await $api.$put<PutSessionStoreResponse>('me/session/store', { storeCode });
      setSessionAuchanStore(result);
    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error });
    }
    await userStore.refreshProductsData();
    setSessionAuchanStoreInProgress(false);
  }

  async function revokeSessionAuchanStore () {
    const {
      $api,
      $logger,
      $localePath,
    } = nuxtApp;
    try {
      setSessionAuchanStoreInProgress(true);
      await $api.$delete('me/session/store');
      setSessionAuchanStore(null);
    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error });
    }
    navigateTo($localePath('/'));
    setSessionAuchanStoreInProgress(false);
  }

  return {
    dealerUserStore,
    stores,
    sessionStore,
    sessionStoreInProgress,
    setDealerUserStore,
    setAuchanStores,
    setSessionAuchanStore,
    setSessionAuchanStoreInProgress,
    fetchDealerUserStore,
    fetchAuchanStores,
    fetchSessionAuchanStore,
    pushSessionAuchanStore,
    revokeSessionAuchanStore,
  };

});
